import React, { useEffect, useState } from 'react';
import {
  ClockIcon,
  ExclamationCircleIcon,
  PauseIcon,
  PlayIcon,
  PlusIcon,
  StopIcon,
} from '@heroicons/react/24/outline';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { supabase } from '../lib/supabase';
import { useCompanyData } from '../hooks/useCompanyData';
import { Database } from "../types/database.types";
import TimeEntryFilters from '../components/TimeEntryFilters';
import TimeEntryList from '../components/TimeEntryList';
import TimeEntryForm from '../components/TimeEntryForm';
import { useTimerStore } from '../stores/timerStore';

type TimeEntryRow = Database['public']['Tables']['time_entries']['Row'];

interface TimeEntryFormData {
  date: string;
  start_time: string;
  end_time: string;
  break_duration: number;
  project_id: string;
  employee_id: string;
  company_id: string;
  activity_id?: string | null;
  cost_category_id?: string | null;
  notes?: string | null;
  hourly_rate?: number | null;
  status?: 'draft' | 'approved' | 'rejected'; // 🆕 VOEG DEZE REGEL TOE
}

type TimeEntry = TimeEntryRow & {
  projects?: { name: string; hourly_rate: number | null } | null;
  employees?: {
    full_name: string;
    hourly_rate: number | null;
  } | null;
  activities?: { name: string } | null;
  cost_categories?: { name: string } | null;
};

const TimeEntryPage: React.FC = () => {
  const queryClient = useQueryClient();
  const [view, setView] = useState<'timer' | 'entries'>('entries');
  const [searchTerm, setSearchTerm] = useState('');
  const [dateRange, setDateRange] = useState('week');
  const [editingEntry, setEditingEntry] = useState<TimeEntry | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [timerDisplay, setTimerDisplay] = useState(0);
  const [showProjectSelect, setShowProjectSelect] = useState(false);
  const [selectedProject, setSelectedProject] = useState('');
  const [selectedActivity, setSelectedActivity] = useState('');
  const [timerDescription, setTimerDescription] = useState('');

  const {
    isRunning,
    isPaused,
    startTime,
    projectId: activeProjectId,
    activityId: activeActivityId,
    description: activeDescription,
    startTimer,
    stopTimer,
    pauseTimer,
    resumeTimer,
    getElapsedSeconds,
  } = useTimerStore();

  const { data: companyId, isLoading: companyLoading } = useQuery<string | null>({
    queryKey: ['companyId'],
    queryFn: async () => {
      const { data, error } = await supabase.rpc('get_current_user_company_id');
      if (error) {
        console.error('Error fetching company ID:', error);
        throw error;
      }
      return data as string | null;
    },
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
    refetchOnWindowFocus: false
  });

  const {
    data: companyData,
    isLoading: dataLoading,
    isError: dataError
  } = useCompanyData(companyId || undefined, dateRange);

  const {
    projects = [],
    employees = [],
    activities = [],
    costCategories = [],
    timeEntries = []
  } = companyData || {};

  useEffect(() => {
    let interval: NodeJS.Timeout | null = null;
    if (isRunning) {
      interval = setInterval(() => {
        setTimerDisplay(getElapsedSeconds());
      }, 1000);
    }
    return () => {
      if (interval) clearInterval(interval);
    };
  }, [isRunning, getElapsedSeconds]);

  const formatTimerDisplay = (seconds: number): string => {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;
    return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  const formatTime24Hour = (timestamp: number): string => {
    const date = new Date(timestamp);
    return date.toLocaleTimeString('nl-NL', {
      hour12: false,
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const handleStartTimer = () => {
    if (!selectedProject || !selectedActivity) {
      toast.error('Selecteer eerst een project en activiteit');
      return;
    }
    startTimer(selectedProject, selectedActivity, timerDescription);
    setShowProjectSelect(false);
    toast.success('Timer gestart!');
  };

  const handleStopTimerAndSave = async () => {
    if (!companyId) {
      toast.error('Geen bedrijf gevonden');
      return;
    }

    const { data: userData } = await supabase.auth.getUser();
    if (!userData?.user) {
      toast.error('Niet ingelogd');
      return;
    }

    const { data: employeeData, error: employeeError } = await supabase
      .from('employees')
      .select('id')
      .eq('user_id', userData.user.id)
      .maybeSingle();

    if (employeeError || !employeeData) {
      toast.error('Geen medewerker gevonden');
      return;
    }

    const { totalBreakDuration } = stopTimer();

    if (!startTime || !activeProjectId || !activeActivityId) {
      toast.error('Timer data onvolledig');
      return;
    }

    const breakDurationMinutes = Math.floor(totalBreakDuration / 60);
    const startTimeStr = formatTime24Hour(startTime);
    const endTimeStr = formatTime24Hour(Date.now());
    const dateStr = new Date().toISOString().split('T')[0];

    const timeEntryData: TimeEntryFormData = {
      date: dateStr,
      start_time: startTimeStr,
      end_time: endTimeStr,
      break_duration: breakDurationMinutes,
      project_id: activeProjectId,
      employee_id: employeeData.id,
      company_id: companyId,
      activity_id: activeActivityId,
      notes: activeDescription || null,
      cost_category_id: null,
      hourly_rate: null,
      status: 'draft', // 🆕 VOEG DEZE REGEL TOE
    };

    try {
      const { error } = await supabase
        .from('time_entries')
        .insert([timeEntryData])
        .select();

      if (error) throw error;

      queryClient.invalidateQueries({ queryKey: ['companyData'] });
      toast.success('Time entry opgeslagen!');
      setTimerDisplay(0);
      setSelectedProject('');
      setSelectedActivity('');
      setTimerDescription('');
    } catch (error: any) {
      toast.error('Opslaan mislukt: ' + error.message);
    }
  };

  const createMutation = useMutation({
    mutationFn: async (formData: TimeEntryFormData) => {
      if (!companyId) throw new Error('Company ID is required');
      const { data, error } = await supabase
        .from('time_entries')
        .insert([{ ...formData, company_id: companyId }])
        .select()
        .single();
      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['companyData'] });
      toast.success('Opgeslagen!');
      setShowForm(false);
    },
    onError: (error: any) => toast.error('Opslaan mislukt: ' + error.message)
  });

  const updateMutation = useMutation({
    mutationFn: async ({ id, data }: { id: string; data: Partial<TimeEntryFormData> }) => {
      const { error } = await supabase
        .from('time_entries')
        .update(data)
        .eq('id', id);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['companyData'] });
      toast.success('Bijgewerkt!');
      setEditingEntry(null);
      setShowForm(false);
    },
    onError: (error: any) => toast.error('Bijwerken mislukt: ' + error.message)
  });

  const deleteMutation = useMutation({
    mutationFn: async (entryId: string) => {
      const { error } = await supabase.from('time_entries').delete().eq('id', entryId);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['companyData'] });
      toast.success('Verwijderd!');
    },
    onError: (error: any) => toast.error('Verwijderen mislukt: ' + error.message)
  });

  const parseTimeString = (timeStr: string) => {
    const [hours, minutes] = timeStr.split(':').map(Number);
    return hours * 60 + minutes;
  };

  const handleFormSubmit = (data: TimeEntryFormData) => {
    if (editingEntry) {
      updateMutation.mutate({ id: editingEntry.id, data });
    } else {
      createMutation.mutate(data);
    }
  };

  const handleEdit = (entry: TimeEntry) => {
    setEditingEntry(entry);
    setShowForm(true);
  };

  const handleDelete = (entryId: string) => {
    if (window.confirm('Weet u zeker dat u deze boeking wilt verwijderen?')) {
      deleteMutation.mutate(entryId);
    }
  };

  const handleDuplicate = (entry: TimeEntry) => {
    setEditingEntry(null);
    const duplicateEntry = {
      ...entry,
      id: 'duplicate',
      date: new Date().toISOString().split('T')[0]
    };
    setEditingEntry(duplicateEntry as TimeEntry);
    setShowForm(true);
  };

  const filteredEntries = React.useMemo(() => {
    return (timeEntries || []).filter((entry: TimeEntry) =>
      entry.projects?.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      entry.employees?.full_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      entry.notes?.toLowerCase().includes(searchTerm.toLowerCase())
    );
  }, [timeEntries, searchTerm]);

  if (companyLoading || dataLoading) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="flex items-center justify-center min-h-[400px]">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto mb-4"></div>
            <p className="text-sm md:text-base text-gray-600 dark:text-gray-400">Gegevens worden geladen...</p>
          </div>
        </div>
      </div>
    );
  }

  if (dataError) {
    return (
      <div className="container mx-auto px-4 py-4 md:py-8">
        <div className="bg-red-50 dark:bg-red-900/10 border border-red-200 dark:border-red-800 rounded-lg p-4 md:p-6">
          <div className="flex items-start md:items-center">
            <ExclamationCircleIcon className="h-5 w-5 md:h-6 md:w-6 text-red-500 mr-2 flex-shrink-0 mt-0.5 md:mt-0" />
            <div>
              <h3 className="text-base md:text-lg font-semibold text-red-800 dark:text-red-200">Er is een fout opgetreden</h3>
              <p className="text-sm md:text-base text-red-700 dark:text-red-300 mt-1">
                De gegevens konden niet worden geladen. Probeer de pagina te verversen.
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!companyId && !companyLoading) {
    return (
      <div className="container mx-auto px-4 py-4 md:py-8">
        <div className="bg-yellow-50 dark:bg-yellow-900/10 border border-yellow-200 dark:border-yellow-800 rounded-lg p-4 md:p-6">
          <div className="flex items-start md:items-center">
            <ExclamationCircleIcon className="h-5 w-5 md:h-6 md:w-6 text-yellow-500 mr-2 flex-shrink-0 mt-0.5 md:mt-0" />
            <span className="text-sm md:text-base text-yellow-800 dark:text-yellow-200">
              Geen bedrijf gevonden. Een beheerder moet uw account koppelen aan een bedrijf.
            </span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-4 md:py-8">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6 md:mb-8">
        <div className="flex items-center">
          <ClockIcon className="h-6 w-6 md:h-8 md:w-8 text-blue-500 mr-2 md:mr-3" />
          <h1 className="text-2xl md:text-3xl font-bold text-gray-800 dark:text-white">Time Tracking</h1>
        </div>

        <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3">
          <div className="flex bg-gray-100 dark:bg-gray-700 rounded-lg p-1">
            <button
              onClick={() => setView('timer')}
              className={`flex-1 sm:flex-initial px-3 sm:px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                view === 'timer'
                  ? 'bg-white dark:bg-gray-600 text-gray-900 dark:text-white shadow'
                  : 'text-gray-600 dark:text-gray-400'
              }`}
            >
              Timer
            </button>
            <button
              onClick={() => setView('entries')}
              className={`flex-1 sm:flex-initial px-3 sm:px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                view === 'entries'
                  ? 'bg-white dark:bg-gray-600 text-gray-900 dark:text-white shadow'
                  : 'text-gray-600 dark:text-gray-400'
              }`}
            >
              Entries
            </button>
          </div>

          <button
            onClick={() => {
              setShowForm(true);
              setEditingEntry(null);
            }}
            className="flex items-center justify-center px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm md:text-base font-medium"
          >
            <PlusIcon className="h-4 w-4 md:h-5 md:w-5 mr-2" />
            <span className="whitespace-nowrap">Nieuwe Entry</span>
          </button>
        </div>
      </div>

      {view === 'timer' && (
        <div className="max-w-4xl mx-auto space-y-4 md:space-y-6">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-4 md:p-6 mb-6 md:mb-8">
            <h3 className="text-lg md:text-xl font-semibold text-gray-800 dark:text-white mb-4 md:mb-6">Timer</h3>

            <div className="text-center mb-6 md:mb-8">
              <div className="bg-gradient-to-br from-blue-50 to-cyan-50 dark:from-blue-900/20 dark:to-cyan-900/20 rounded-2xl p-6 md:p-8 mb-4 md:mb-6">
                <div className="text-4xl md:text-6xl font-mono font-bold text-gray-800 dark:text-white mb-2">
                  {formatTimerDisplay(timerDisplay)}
                </div>
                {isRunning && (
                  <div className="text-xs md:text-sm text-gray-600 dark:text-gray-400">
                    {isPaused ? '⏸️ Gepauzeerd' : '▶️ Loopt'}
                    {startTime && (
                      <span className="ml-2">
                        • Gestart om {formatTime24Hour(startTime)}
                      </span>
                    )}
                  </div>
                )}
                {isRunning && activeProjectId && (
                  <div className="mt-3 text-sm md:text-base text-gray-700 dark:text-gray-300">
                    <div className="font-medium">
                      {projects.find(p => p.id === activeProjectId)?.name || 'Project'}
                    </div>
                    {activeActivityId && (
                      <div className="text-xs md:text-sm text-gray-600 dark:text-gray-400">
                        {activities.find(a => a.id === activeActivityId)?.name || 'Activiteit'}
                      </div>
                    )}
                  </div>
                )}
              </div>

              {!isRunning && showProjectSelect && (
                <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4 mb-4 text-left space-y-3">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Project
                    </label>
                    <select
                      value={selectedProject}
                      onChange={(e) => setSelectedProject(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    >
                      <option value="">Selecteer project...</option>
                      {projects.map((project) => (
                        <option key={project.id} value={project.id}>
                          {project.name}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Activiteit
                    </label>
                    <select
                      value={selectedActivity}
                      onChange={(e) => setSelectedActivity(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    >
                      <option value="">Selecteer activiteit...</option>
                      {activities.map((activity) => (
                        <option key={activity.id} value={activity.id}>
                          {activity.name}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Beschrijving (optioneel)
                    </label>
                    <input
                      type="text"
                      value={timerDescription}
                      onChange={(e) => setTimerDescription(e.target.value)}
                      placeholder="Extra notities..."
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>
                </div>
              )}

              <div className="flex justify-center space-x-3 md:space-x-4">
                {!isRunning ? (
                  showProjectSelect ? (
                    <>
                      <button
                        onClick={() => setShowProjectSelect(false)}
                        className="flex items-center px-4 md:px-6 py-2 md:py-3 bg-gray-500 text-white rounded-lg hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-gray-500 text-sm md:text-base font-medium"
                      >
                        Annuleer
                      </button>
                      <button
                        onClick={handleStartTimer}
                        className="flex items-center px-4 md:px-6 py-2 md:py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500 text-sm md:text-base font-medium"
                      >
                        <PlayIcon className="h-4 w-4 md:h-5 md:w-5 mr-2" />
                        Start Timer
                      </button>
                    </>
                  ) : (
                    <button
                      onClick={() => setShowProjectSelect(true)}
                      className="flex items-center px-4 md:px-6 py-2 md:py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500 text-sm md:text-base font-medium"
                    >
                      <PlayIcon className="h-4 w-4 md:h-5 md:w-5 mr-2" />
                      Start Timer
                    </button>
                  )
                ) : (
                  <>
                    {isPaused ? (
                      <button
                        onClick={resumeTimer}
                        className="flex items-center px-4 md:px-6 py-2 md:py-3 bg-green-600 hover:bg-green-700 text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 text-sm md:text-base font-medium"
                      >
                        <PlayIcon className="h-4 w-4 md:h-5 md:w-5 mr-2" />
                        Hervat
                      </button>
                    ) : (
                      <button
                        onClick={pauseTimer}
                        className="flex items-center px-4 md:px-6 py-2 md:py-3 bg-yellow-600 hover:bg-yellow-700 text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-yellow-500 text-sm md:text-base font-medium"
                      >
                        <PauseIcon className="h-4 w-4 md:h-5 md:w-5 mr-2" />
                        Pauze
                      </button>
                    )}
                    <button
                      onClick={handleStopTimerAndSave}
                      className="flex items-center px-4 md:px-6 py-2 md:py-3 bg-red-600 text-white rounded-lg hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 text-sm md:text-base font-medium"
                    >
                      <StopIcon className="h-4 w-4 md:h-5 md:w-5 mr-2" />
                      Stop & Sla Op
                    </button>
                  </>
                )}
              </div>
            </div>
          </div>

          <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-4 md:p-6">
            <h3 className="text-base md:text-lg font-semibold text-gray-800 dark:text-white mb-3 md:mb-4">
              Recente Entries
            </h3>
            {filteredEntries.slice(0, 3).map((entry: TimeEntry) => {
              const entryDate = new Date(entry.date);
              const hours = entry.end_time && entry.start_time
                ? (() => {
                    const start = parseTimeString(entry.start_time);
                    const end = parseTimeString(entry.end_time);
                    let total = end - start;
                    if (total < 0) total += 24 * 60;
                    const net = total - (entry.break_duration || 0);
                    const h = Math.floor(net / 60).toString().padStart(2, '0');
                    const m = (net % 60).toString().padStart(2, '0');
                    return `${h}:${m}`;
                  })()
                : '00:00';

              return (
                <div
                  key={entry.id}
                  className="flex flex-col sm:flex-row sm:items-center sm:justify-between py-3 border-b dark:border-gray-700 last:border-0 gap-2"
                >
                  <div className="flex items-center space-x-3 sm:space-x-4">
                    <div className="text-xs sm:text-sm text-gray-600 dark:text-gray-400 min-w-[3rem]">
                      {entryDate.toLocaleDateString('nl-NL', { weekday: 'short' })}
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="font-medium text-sm sm:text-base text-gray-900 dark:text-white truncate">
                        {entry.projects?.name}
                      </p>
                      <p className="text-xs sm:text-sm text-gray-600 dark:text-gray-400">
                        {entry.start_time?.substring(0, 5)} - {entry.end_time?.substring(0, 5)}
                      </p>
                    </div>
                  </div>
                  <div className="text-base sm:text-lg font-semibold text-blue-600 dark:text-blue-400 self-end sm:self-auto">
                    {hours}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {view === 'entries' && (
        <div className="space-y-4">
          <TimeEntryFilters
            searchTerm={searchTerm}
            onSearchChange={setSearchTerm}
            dateRange={dateRange}
            onDateRangeChange={setDateRange}
            totalEntries={timeEntries.length}
            filteredCount={filteredEntries.length}
          />

          <TimeEntryList
            entries={filteredEntries as any}
            onEdit={handleEdit as any}
            onDelete={handleDelete}
            onDuplicate={handleDuplicate as any}
            isLoading={dataLoading}
            searchTerm={searchTerm}
            onNewEntry={() => {
              setShowForm(true);
              setEditingEntry(null);
            }}
          />
        </div>
      )}

      <TimeEntryForm
        isOpen={showForm}
        onClose={() => {
          setShowForm(false);
          setEditingEntry(null);
        }}
        onSubmit={handleFormSubmit}
        editingEntry={editingEntry}
        projects={projects}
        employees={employees as any}
        activities={activities}
        costCategories={costCategories}
        isSubmitting={createMutation.isPending || updateMutation.isPending}
        companyId={companyId || undefined}
      />
    </div>
  );
};

export default TimeEntryPage;
